import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ShoppingCart, PackageMinus, CheckCircle, ArrowRight } from 'lucide-react';
import { ManagementMode, useManagementMode } from '@/hooks/useManagementMode';
import { useNavigate } from 'react-router-dom';

export const ManagementModeSelection: React.FC = () => {
  const [selectedMode, setSelectedMode] = useState<ManagementMode>(null);
  const { updateMode, loading } = useManagementMode();
  const navigate = useNavigate();

  const handleConfirm = async () => {
    if (!selectedMode) return;
    
    const success = await updateMode(selectedMode);
    if (success) {
      // Rediriger vers la page correspondant au mode choisi
      navigate(selectedMode === 'caisse' ? '/caisse' : '/retrait');
    }
  };

  const modes = [
    {
      id: 'caisse' as const,
      title: 'Mode Caisse',
      description: 'Gestion des ventes et encaissements',
      icon: ShoppingCart,
      color: 'from-green-500 to-green-600',
      hoverColor: 'hover:from-green-600 hover:to-green-700',
      features: [
        'Interface de vente rapide',
        'Gestion des paiements',
        'Analyse des ventes',
        'Historique des transactions'
      ]
    },
    {
      id: 'retrait' as const,
      title: 'Mode Retrait',
      description: 'Gestion des sorties de stock',
      icon: PackageMinus,
      color: 'from-orange-500 to-orange-600',
      hoverColor: 'hover:from-orange-600 hover:to-orange-700',
      features: [
        'Retrait de produits',
        'Suivi des sorties',
        'Analyse des retraits',
        'Gestion des motifs'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-4xl">
        {/* En-tête */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-4">
            Bienvenue dans Chart-Flow
          </h1>
          <p className="text-xl text-muted-foreground mb-2">
            Choisissez votre mode de gestion
          </p>
          <p className="text-sm text-muted-foreground max-w-2xl mx-auto">
            Sélectionnez le mode qui correspond à votre utilisation principale. 
            Cette configuration adaptera l'interface à vos besoins spécifiques.
          </p>
        </div>

        {/* Sélection des modes */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {modes.map((mode) => {
            const Icon = mode.icon;
            const isSelected = selectedMode === mode.id;
            
            return (
              <Card 
                key={mode.id}
                className={`
                  relative cursor-pointer transition-all duration-300 transform hover:scale-105 hover:shadow-xl
                  ${isSelected ? 'ring-4 ring-primary ring-opacity-50 shadow-lg' : 'hover:shadow-md'}
                `}
                onClick={() => setSelectedMode(mode.id)}
              >
                <CardHeader className="relative overflow-hidden">
                  {/* Gradient de fond */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${mode.color} opacity-10`} />
                  
                  <div className="relative flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`p-4 rounded-xl bg-gradient-to-br ${mode.color} text-white shadow-lg`}>
                        <Icon className="w-8 h-8" />
                      </div>
                      <div>
                        <CardTitle className="text-xl">{mode.title}</CardTitle>
                        <p className="text-sm text-muted-foreground">{mode.description}</p>
                      </div>
                    </div>
                    
                    {isSelected && (
                      <div className="p-2 rounded-full bg-green-100 dark:bg-green-900">
                        <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
                      </div>
                    )}
                  </div>
                </CardHeader>

                <CardContent className="relative">
                  <div className="space-y-3">
                    <h4 className="font-semibold text-sm">Fonctionnalités incluses :</h4>
                    <ul className="space-y-2">
                      {mode.features.map((feature, index) => (
                        <li key={index} className="flex items-center gap-2 text-sm">
                          <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Effet de sélection */}
                  {isSelected && (
                    <div className="absolute inset-0 bg-primary/5 rounded-lg pointer-events-none" />
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Bouton de confirmation */}
        <div className="flex justify-center">
          <Button
            onClick={handleConfirm}
            disabled={!selectedMode || loading}
            className={`
              px-8 py-3 text-lg font-semibold min-w-[200px] transition-all duration-300
              ${selectedMode 
                ? 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 shadow-lg hover:shadow-xl transform hover:scale-105' 
                : 'opacity-50 cursor-not-allowed'
              }
            `}
          >
            {loading ? (
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Configuration...
              </div>
            ) : (
              <div className="flex items-center gap-2">
                Confirmer mon choix
                <ArrowRight className="w-5 h-5" />
              </div>
            )}
          </Button>
        </div>

        {/* Note importante */}
        <div className="mt-8 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
          <p className="text-sm text-amber-800 dark:text-amber-200 text-center">
            <strong>Note :</strong> Ce choix déterminera l'interface et les fonctionnalités disponibles. 
            Il ne pourra pas être modifié ultérieurement.
          </p>
        </div>
      </div>
    </div>
  );
};